/**
 * Order Status Slider
 *
 * Horizontal slider with draggable progress line.
 * Clean design with icons and labels.
 */

import { useCallback, useRef, useEffect, useState } from "react";
import {
  IconClock,
  IconHourglass,
  IconCheck,
  IconTicket,
  IconX,
  IconLayers,
} from "../../../../components/Icons.tsx";
import { getTelegram } from "../../../../lib/telegram.ts";

// =============================================================================
// TYPES & CONFIG
// =============================================================================

export type OrderLifecycleStatus =
  | "all"
  | "awaiting_payment"
  | "pending_verification"
  | "verified"
  | "ticket_issued"
  | "rejected";

/** Valid lifecycle status values for runtime validation */
export const VALID_LIFECYCLE_STATUSES: readonly OrderLifecycleStatus[] = [
  "all",
  "awaiting_payment",
  "pending_verification",
  "verified",
  "ticket_issued",
  "rejected",
] as const;

/** Type guard for OrderLifecycleStatus */
export function isValidLifecycleStatus(
  value: unknown
): value is OrderLifecycleStatus {
  return (
    typeof value === "string" &&
    VALID_LIFECYCLE_STATUSES.includes(value as OrderLifecycleStatus)
  );
}

export interface OrderStatusSliderProps {
  value: OrderLifecycleStatus;
  onChange: (status: OrderLifecycleStatus) => void;
  counts?: Partial<Record<OrderLifecycleStatus, number>>;
  className?: string;
}

interface StageConfig {
  status: OrderLifecycleStatus;
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  color: string;
  bgActive: string;
}

// Stages on the slider line (happy path)
const SLIDER_STAGES: StageConfig[] = [
  {
    status: "awaiting_payment",
    Icon: IconClock,
    label: "Ожидает",
    color: "amber",
    bgActive: "bg-amber-500",
  },
  {
    status: "pending_verification",
    Icon: IconHourglass,
    label: "Проверка",
    color: "orange",
    bgActive: "bg-orange-500",
  },
  {
    status: "verified",
    Icon: IconCheck,
    label: "Подтв.",
    color: "emerald",
    bgActive: "bg-emerald-500",
  },
  {
    status: "ticket_issued",
    Icon: IconTicket,
    label: "Выданы",
    color: "teal",
    bgActive: "bg-teal-500",
  },
];

function triggerHaptic() {
  const tg = getTelegram();
  if (tg?.HapticFeedback) {
    tg.HapticFeedback.impactOccurred("light");
  } else if ("vibrate" in navigator) {
    navigator.vibrate(10);
  }
}

// =============================================================================
// COMPONENT
// =============================================================================

export function OrderStatusSlider({
  value,
  onChange,
  counts,
  className = "",
}: OrderStatusSliderProps) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const prevValueRef = useRef(value);

  const activeSliderIndex = SLIDER_STAGES.findIndex((s) => s.status === value);
  const isSliderActive = activeSliderIndex !== -1;
  const isAllActive = value === "all";
  const isRejectedActive = value === "rejected";

  const getCount = (status: OrderLifecycleStatus) => counts?.[status] ?? 0;

  // Haptic on change
  useEffect(() => {
    if (prevValueRef.current !== value) {
      triggerHaptic();
      prevValueRef.current = value;
    }
  }, [value]);

  const handleClick = useCallback(
    (status: OrderLifecycleStatus) => {
      if (status !== value) onChange(status);
    },
    [onChange, value],
  );

  // Drag on slider
  const handleSliderInteraction = useCallback(
    (clientX: number) => {
      if (!sliderRef.current) return;
      const rect = sliderRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      const idx = Math.round(pct * (SLIDER_STAGES.length - 1));
      const stage = SLIDER_STAGES[idx];
      if (stage && stage.status !== value) onChange(stage.status);
    },
    [onChange, value],
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setIsDragging(true);
      handleSliderInteraction(e.clientX);
    },
    [handleSliderInteraction],
  );

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      setIsDragging(true);
      handleSliderInteraction(e.touches[0].clientX);
    },
    [handleSliderInteraction],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (isDragging) handleSliderInteraction(e.touches[0].clientX);
    },
    [isDragging, handleSliderInteraction],
  );

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => handleSliderInteraction(e.clientX);
    const onUp = () => setIsDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging, handleSliderInteraction]);

  // Progress line width
  const progressPct = isSliderActive
    ? (activeSliderIndex / (SLIDER_STAGES.length - 1)) * 100
    : 0;

  const activeStage = isSliderActive ? SLIDER_STAGES[activeSliderIndex] : null;

  return (
    <div className={`py-2.5 ${className}`}>
      <div className="flex items-start gap-2">
        {/* All button */}
        <button
          type="button"
          onClick={() => handleClick("all")}
          className={`shrink-0 flex flex-col items-center w-10 transition-all active:scale-95 focus:outline-none`}
        >
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
              isAllActive
                ? "bg-slate-700 text-white shadow-md ring-2 ring-white"
                : "bg-white border-2 border-slate-200 text-slate-400"
            }`}
          >
            <IconLayers className="w-4 h-4" />
          </div>
          <span
            className={`text-[9px] font-medium mt-1.5 ${isAllActive ? "text-slate-700" : "text-slate-400"}`}
          >
            Все
          </span>
        </button>

        {/* Slider */}
        <div
          ref={sliderRef}
          className="flex-1 relative h-14 cursor-pointer select-none touch-none"
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={() => setIsDragging(false)}
        >
          {/* Track background */}
          <div className="absolute left-3 right-3 top-[18px] h-[3px] bg-slate-200 rounded-full" />

          {/* Progress line */}
          {isSliderActive && activeStage && (
            <div
              className={`absolute top-[18px] h-[3px] rounded-full transition-all duration-200 ${activeStage.bgActive}`}
              style={{
                left: "12px",
                width: `calc((100% - 24px) * ${progressPct / 100})`,
              }}
            />
          )}

          {/* Points */}
          <div className="absolute inset-x-0 top-0 flex justify-between">
            {SLIDER_STAGES.map((stage, i) => {
              const Icon = stage.Icon;
              const isActive = isSliderActive && i === activeSliderIndex;
              const isPast = isSliderActive && i < activeSliderIndex;
              const count = getCount(stage.status);

              return (
                <button
                  key={stage.status}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick(stage.status);
                  }}
                  className="relative flex flex-col items-center w-10 focus:outline-none"
                >
                  {/* Dot */}
                  <div
                    className={`rounded-full flex items-center justify-center transition-all duration-200 ${
                      isActive
                        ? `w-9 h-9 ${stage.bgActive} text-white shadow-md ring-2 ring-white`
                        : isPast
                          ? `w-7 h-7 ${stage.bgActive} text-white`
                          : "w-7 h-7 bg-white border-2 border-slate-200 text-slate-400"
                    }`}
                  >
                    <Icon className={isActive ? "w-4 h-4" : "w-3.5 h-3.5"} />
                  </div>
                  {/* Label */}
                  <span
                    className={`text-[9px] font-medium mt-1.5 ${
                      isActive || isPast ? "text-slate-700" : "text-slate-400"
                    }`}
                  >
                    {stage.label}
                  </span>
                  {/* Badge */}
                  {!isActive && count > 0 && (
                    <span className="absolute -top-0.5 right-0 min-w-[14px] h-[14px] px-0.5 rounded-full bg-slate-600 text-[8px] font-semibold text-white flex items-center justify-center">
                      {count > 99 ? "99" : count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Rejected button */}
        <button
          type="button"
          onClick={() => handleClick("rejected")}
          className={`shrink-0 flex flex-col items-center w-10 transition-all active:scale-95 focus:outline-none`}
        >
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
              isRejectedActive
                ? "bg-red-500 text-white shadow-md ring-2 ring-white"
                : "bg-white border-2 border-slate-200 text-slate-400"
            }`}
          >
            <IconX className="w-4 h-4" />
          </div>
          <span
            className={`text-[9px] font-medium mt-1.5 ${isRejectedActive ? "text-slate-700" : "text-slate-400"}`}
          >
            Откл.
          </span>
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// HELPERS
// =============================================================================

export function mapLifecycleToBackendStatus(
  status: OrderLifecycleStatus,
): string[] {
  switch (status) {
    case "all":
      return [];
    case "awaiting_payment":
      return ["AWAITING_PAYMENT"];
    case "pending_verification":
      return ["PENDING_VERIFICATION"];
    case "verified":
      return ["VERIFIED"];
    case "ticket_issued":
      return ["TICKET_ISSUED"];
    case "rejected":
      return ["REJECTED", "CANCELLED", "EXPIRED"];
    default:
      return [];
  }
}

export function getBackendStatusFilter(status: OrderLifecycleStatus): string {
  switch (status) {
    case "awaiting_payment":
      return "AWAITING_PAYMENT";
    case "pending_verification":
      return "PENDING_VERIFICATION";
    case "verified":
      return "VERIFIED";
    case "ticket_issued":
      return "TICKET_ISSUED";
    default:
      return "";
  }
}

/** For verified tab: VERIFIED + TICKETS_PENDING_SEND (awaiting admin action) */
export function matchesVerifiedFilter(status: string): boolean {
  return status === "VERIFIED" || status === "TICKETS_PENDING_SEND";
}
